import { useRef, useState } from "react";
import { FilterPanel, Button, Title } from "../../components";
import logger from "../../utils/logger";
import CreateExportModal from "./components/CreateExportModal";
import ExportTable from "./components/ExportsTable";
import useTableData from "../../hooks/useTableData";
import { useExportService } from "./ExportServiceContext";
import { format } from 'date-fns-tz';
import "./index.css";
import { events } from "../../utils/constants";
import ToastService from "../../services/toast.service";

function ExportPage() {
  const ExportService = useExportService();
  const {
    data,
    sortColumn,
    sortType,
    loading,
    activeFilters,
    filtersRef,
    currentPage,
    pageSize,
    totalItems,
    handleSortColumn,
    applyFilters,
    getColumnValues,
    setFilterValues,
    getFilterValues,
    handlePageChange,
    handlePageSizeChange,
  } = useTableData({
    service: ExportService,
    initialFilters: {
      postingDate: [],
      createdAt: [],
    },
    hasDateRange: false,
    entityName: "exports",
    serviceUpdateEvent: events.EXPORT_UPDATED,
    filterableColumns: ["postingDate", "createdAt"],
    searchableColumns: [],
    initialSortColumn: "createdAt",
    initialSortType: "desc",
  });

  const activeFilterColumnKey = useRef(null);
  const [isCheckPickerOpen, setIsCheckPickerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const openCheckPickerForColumn = (columnKey) => {
    activeFilterColumnKey.current = columnKey;

    // For date columns, ensure we have a proper initial value
    const currentFilterValue = filtersRef.current[columnKey] || [];
    setFilterValues(currentFilterValue);
    setIsCheckPickerOpen(true);
  };

  const applyCurrentFilters = () => {
    const selectedValuesFromPanel = getFilterValues();
    filtersRef.current = {
      ...filtersRef.current,
      [activeFilterColumnKey.current]: selectedValuesFromPanel,
    };
    applyFilters();
    setIsCheckPickerOpen(false);
  };

  function onFilterChange(selectedValues) {
    setFilterValues(selectedValues);
  }

  const cancelFiltering = () => {
    setIsCheckPickerOpen(false);
  };

  const getValuesForActiveColumn = () => {
    if (!activeFilterColumnKey.current) return [];
    return getColumnValues(activeFilterColumnKey.current);
  };

  function handleExport(date) {
    setExportLoading(true);
    const formattedDate = format(date, 'yyyy-MM-dd', { timeZone: 'Pacific/Auckland' });
    ExportService.createExport(formattedDate)
      .then(async () => {
        logger.log("Export created successfully");
        ToastService.success("Export created successfully");
      })
      .catch((error) => {
        logger.error("Error creating export:", error);
        ToastService.error(`Failed to create export: ${error.message || "Unknown error"}`);
      })
      .finally(() => {
        setExportLoading(false);
      });
  }

  return (
    <div className="export-page">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
          width: "100%",
        }}
      >
        <Title text="WINZ SUBSIDY PAYMENT EXPORT" />
        <Button
          type="button"
          onClick={() => { console.log('open Create'); setModalOpen(true); }}
          text="Create Export"
        />
      </div>
      {isCheckPickerOpen && activeFilterColumnKey.current && (
        <FilterPanel
          activeColumn={activeFilterColumnKey.current}
          filterOptions={getValuesForActiveColumn()}
          selectedValues={getFilterValues()}
          onApply={applyCurrentFilters}
          onCancel={cancelFiltering}
          onFilterChange={onFilterChange}
        />
      )}
      <ExportTable
        data={data}
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={handleSortColumn}
        loading={exportLoading || loading}
        activeFilters={activeFilters}
        onFilterClick={openCheckPickerForColumn}
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
      <CreateExportModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onExport={handleExport}
      />
    </div>
  );
}

export default ExportPage;
